import React, { useState } from "react";
import dayjs from "dayjs";

export interface ActionField {
  label: string;
  value?: string | null;
  isDate?: boolean;
}

interface CaseActionModalProps {
  isOpen: boolean;
  title: string;
  confirmLabel: string;
  confirmButtonClass?: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  baseFields: ActionField[];
  extraFields: ActionField[];
  loading?: boolean;
  reasonLabel?: string;
}

const formatValue = (field: ActionField) =>
  field.isDate
    ? field.value
      ? dayjs(field.value).format("DD-MM-YYYY")
      : "-"
    : (field.value ?? "-");

const CaseActionModal: React.FC<CaseActionModalProps> = ({
  isOpen,
  title,
  confirmLabel,
  confirmButtonClass = "caf-btn-primary",
  onClose,
  onConfirm,
  baseFields,
  extraFields,
  loading = false,
  reasonLabel = "Enter reason",
}) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError("Please enter a reason.");
      return;
    }

    setError("");
    onConfirm(reason.trim());
  };

  const handleClose = () => {
    setReason("");
    setError("");
    onClose();
  };

  const allFields = [...baseFields, ...extraFields];

  return (
    <>
      <style>{`
       
        .cam-header h5 {
          font-size: 1.05rem;
          color: #1f2430;
        } 
        .cam-close-btn:hover {
          background-color: #fdeaea;
          color: #b3261e;
        }
        
        .cam-summary-table {
          background-color: #fbfbfd;
          border: 1px solid #eef0f3;
          border-radius: 12px;
          overflow: hidden;
        }
        .cam-summary-table td {
          padding: 0.6rem 1rem;
          border-bottom: 1px solid #eef0f3;
        }
        .cam-summary-table tr:last-child td {
          border-bottom: none;
        }
        .cam-summary-label {
          color: #8a90a0 !important;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          font-size: 0.72rem !important;
        }
        .cam-summary-value {
          color: #2b2f38;
          font-size: 0.9rem;
        }
        .cam-reason-label {
          color: #2b2f38;
          font-size: 0.85rem;
          margin-bottom: 0.4rem;
        }
        .cam-textarea {
          border-radius: 10px;
          border: 1.5px solid #e3e5ea;
          padding: 0.7rem 0.9rem;
          font-size: 0.9rem;
          resize: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .cam-textarea:focus {
          border-color: #4338ca;
          box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.12);
        }
       
        .cam-btn {
          border-radius: 9px;
          font-weight: 600;
          font-size: 0.85rem;
          padding: 0.5rem 1.1rem;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cam-btn:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }
      `}</style>

      <div
        className="modal-backdrop fade show cam-backdrop"
        onClick={handleClose}
      />
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content caf-modal-content p-3">
            <div className="modal-header border-0 d-flex flex-column align-items-stretch pb-0 caf-modal-header">
              <div className="caf-modal-header-top">
                <div className="caf-modal-title-group">
                  <div className="caf-modal-title-icon">
                    <i className="ti ti-alert-circle" />
                  </div>

                  <div className="caf-modal-title-text">
                    <h5>{title}</h5>
                    <span>
                      Please review the information before proceeding.
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className="caf-btn-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>

            <div className="caf-section-card">
              <table className="table table-borderless table-sm mb-4 cam-summary-table">
                <tbody>
                  {allFields.map((field) => (
                    <tr key={field.label}>
                      <td
                        className="text-muted fw-semibold cam-summary-label"
                        style={{ width: "40%", padding: "10px 20px" }}
                      >
                        {field.label}
                      </td>

                      <td className="cam-summary-value">
                        {formatValue(field)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ padding: "10px 20px" }}>
                <label className="form-label fw-semibold">{reasonLabel}</label>

                <textarea
                  className={`form-control cam-textarea ${
                    error ? "is-invalid" : ""
                  }`}
                  rows={4}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);

                    if (error) {
                      setError("");
                    }
                  }}
                  placeholder={reasonLabel}
                />

                {error && <div className="invalid-feedback">{error}</div>}
              </div>
            </div>

            <div className="caf-footer-actions">
              <button
                type="button"
                onClick={handleClose}
                className="caf-btn caf-btn-secondary"
                disabled={loading}
              >
                <i className="ti ti-x me-1" />
                Cancel
              </button>

              <button
                type="button"
                className={`caf-btn ${confirmButtonClass}`}
                onClick={handleConfirm}
                disabled={loading || !reason.trim()}
              >
                <i className="ti ti-check me-1" />
                {loading ? "Submitting..." : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CaseActionModal;